import { useState, useEffect } from 'react';
import { apiFetch } from './shared';

const API = '/api';

function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const INPUT = { display:'block', width:'100%', marginTop:4, background:'#111', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' };
const BTN   = (active, color='#2a4a6f') => ({ background: active ? color : '#1a1a1a', color: active ? '#fff' : '#444', border:'none', borderRadius:5, padding:'6px 12px', fontSize:12, cursor: active ? 'pointer' : 'default', fontFamily:'inherit' });

const STATUS_FARBEN = { aktiv: '#5a8fd4', pausiert: '#888', abgeschlossen: '#4a9c6f' };
const PRIORITAET_LABELS = { 1: 'Hauptziel', 2: 'Nebenziel' };


// ─── Wochen-Balken (km + hm getrennt, keine Dual-Achse) ──────────────────────
function WochenBalken({ wochen, feld, farbe, einheit }) {
  const werte = wochen.map(w => w[feld] || 0);
  const max = Math.max(...werte, 1);
  const maxIdx = werte.reduce((best, v, i) => v > werte[best] ? i : best, 0);

  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:36 }}>
      {wochen.map((w, i) => {
        const h = Math.round((w[feld] || 0) / max * 32);
        return (
          <div
            key={w.woche_start}
            title={`KW ab ${w.woche_start}: ${w[feld] || 0}${einheit}`}
            style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', height:36 }}
          >
            {i === maxIdx && werte[i] > 0 && (
              <div style={{ fontSize:8, color: farbe, marginBottom:2 }}>{werte[i]}</div>
            )}
            <div style={{ width:'100%', maxWidth:14, height: Math.max(h, w[feld] > 0 ? 2 : 0), background: farbe, borderRadius:'2px 2px 0 0', opacity: i === maxIdx ? 1 : 0.6 }} />
          </div>
        );
      })}
    </div>
  );
}

// ─── Einzelne Dimension (Score-Zeile mit Mini-Balken) ────────────────────────
function DimensionZeile({ label, score, zusatzText }) {
  if (score == null) return null;
  const farbe = score >= 75 ? '#4a9c6f' : score >= 50 ? '#c17f24' : '#5a8fd4';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#888', marginBottom:3 }}>
        <span>{label}</span>
        <span style={{ color:farbe, fontWeight:600 }}>{score}%</span>
      </div>
      <div style={{ height:4, background:'#1c1c1c', borderRadius:2, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${score}%`, background:farbe, borderRadius:2, transition:'width 0.4s' }} />
      </div>
      {zusatzText && <div style={{ fontSize:10, color:'#555', marginTop:3 }}>{zusatzText}</div>}
    </div>
  );
}

// ─── Fortschritts-Auswertung (Detailkarte, Daten von ZielKarte übergeben) ────
function FortschrittsKarte({ data }) {
  if (!data) return null;

  const {
    ziel_km, ziel_hm, laengste_km, laengste_hm, distanz_pct, hm_pct,
    wochen_volumen, aktuelle_phase, gym_sessions_pro_woche,
    hrv_trend, bereitschafts_score, dimensionen,
  } = data;

  const scoreFarbe = bereitschafts_score >= 75 ? '#4a9c6f' : bereitschafts_score >= 50 ? '#c17f24' : '#5a8fd4';
  const scoreText = bereitschafts_score == null ? null
    : bereitschafts_score >= 75 ? 'Du bist gut vorbereitet.'
    : bereitschafts_score >= 50 ? 'Du näherst dich — weiter so.'
    : 'Die Grundlage wächst von Woche zu Woche.';

  const hatVolumen = wochen_volumen.some(w => w.km > 0 || w.hm > 0);

  const dim1 = dimensionen?.spitzenleistung;
  const dim1Text = dim1?.score == null ? null
    : dim1.score >= 75 ? 'Deine Ausdauer-Basis passt gut zur Distanz.'
    : dim1.score >= 50 ? 'Du näherst dich der Ziel-Distanz.'
    : 'Eine lange Einheit nahe der Zieldistanz fehlt noch.';

  const dim2 = dimensionen?.trainingskontinuitaet;
  const dim2Text = dim2?.score == null ? null
    : (dim2.score >= 75 ? 'Dein Rhythmus ist gerade gut.'
       : dim2.score >= 50 ? 'Solider Rhythmus, etwas mehr Regelmäßigkeit hilft.'
       : 'Der Rhythmus darf noch wachsen.')
      + (dim2.luecke ? ' Der nächste Ride zählt schon wieder.' : '');

  const dim3 = dimensionen?.erholung;
  const dim3Text = dim3?.score == null ? null
    : (dim3.score >= 75 ? 'Du bist gut erholt.'
       : dim3.score >= 50 ? 'Erholung im normalen Rahmen.'
       : 'Gönn dir etwas Ruhe, bevor du pushst.')
      + (dim3.rhr_steigend ? ' Ruhepuls zieht gerade leicht an.' : '');

  return (
    <div style={{ background:'#0a0f14', border:'1px solid #1a2a3a', borderRadius:6, padding:'12px 14px', display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:9, color:'#5a8fd4', letterSpacing:2 }}>FORTSCHRITT</div>

      {/* Distanz / Hm Fortschrittsbalken */}
      {(ziel_km || ziel_hm) && (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {ziel_km && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#555', marginBottom:3 }}>
                <span>Distanz</span>
                <span style={{ color:'#5a8fd4' }}>{distanz_pct != null ? `${distanz_pct}%` : '–'}</span>
              </div>
              <div style={{ height:5, background:'#1c1c1c', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${distanz_pct || 0}%`, background:'#5a8fd4', borderRadius:3, transition:'width 0.4s' }} />
              </div>
              {laengste_km && (
                <div style={{ fontSize:11, color:'#5a8fd4', marginTop:4 }}>
                  {distanz_pct != null
                    ? `${distanz_pct}% der Ziel-Distanz bereits in einem Ride geschafft (${laengste_km} km).`
                    : `Längste Fahrt bisher: ${laengste_km} km.`}
                </div>
              )}
            </div>
          )}
          {ziel_hm && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#555', marginBottom:3 }}>
                <span>Höhenmeter</span>
                <span style={{ color:'#8a6a3a' }}>{hm_pct != null ? `${hm_pct}%` : '–'}</span>
              </div>
              <div style={{ height:5, background:'#1c1c1c', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${hm_pct || 0}%`, background:'#8a6a3a', borderRadius:3, transition:'width 0.4s' }} />
              </div>
              {laengste_hm && (
                <div style={{ fontSize:11, color:'#8a6a3a', marginTop:4 }}>
                  {hm_pct != null
                    ? `${hm_pct}% des Ziel-Anstiegs bereits in einem Ride geschafft (${laengste_hm} hm).`
                    : `Meiste Höhenmeter bisher: ${laengste_hm} hm.`}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Wochen-Volumen Mini-Chart */}
      {hatVolumen && (
        <div>
          <div style={{ fontSize:10, color:'#555', marginBottom:6 }}>Volumen · letzte 8 Wochen</div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <div>
              <div style={{ fontSize:9, color:'#3a5a8a', marginBottom:2 }}>km</div>
              <WochenBalken wochen={wochen_volumen} feld="km" farbe="#5a8fd4" einheit=" km" />
            </div>
            <div>
              <div style={{ fontSize:9, color:'#6a4a2a', marginBottom:2 }}>hm</div>
              <WochenBalken wochen={wochen_volumen} feld="hm" farbe="#8a6a3a" einheit=" hm" />
            </div>
          </div>
        </div>
      )}

      {/* Gym-Konsistenz (Info, fließt nicht in den Score) */}
      {gym_sessions_pro_woche != null && (
        <div style={{ fontSize:11, color:'#555' }}>
          Gym-Konsistenz{aktuelle_phase ? ` (${aktuelle_phase.name})` : ''}: <span style={{ color:'#4a9c6f' }}>Ø {gym_sessions_pro_woche} Sessions/Woche</span>
        </div>
      )}

      {/* Drei Dimensionen des Bereitschafts-Scores */}
      {(dim1?.score != null || dim2?.score != null || dim3?.score != null) && (
        <div style={{ display:'flex', flexDirection:'column', gap:10, paddingTop:8, borderTop:'1px solid #1a2a3a' }}>
          <DimensionZeile label={dim1?.label || 'Ausdauer-Basis'} score={dim1?.score} zusatzText={dim1Text} />
          <DimensionZeile label={dim2?.label || 'Trainingsrhythmus'} score={dim2?.score} zusatzText={dim2Text} />
          <DimensionZeile label={dim3?.label || 'Aktuelle Form'} score={dim3?.score} zusatzText={dim3Text} />
        </div>
      )}

      {/* Bereitschafts-Score (Gesamt) */}
      {bereitschafts_score != null && (
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{
            width:40, height:40, borderRadius:'50%', flexShrink:0,
            border:`2px solid ${scoreFarbe}`, display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:12, fontWeight:700, color:scoreFarbe,
          }}>
            {bereitschafts_score}%
          </div>
          <div style={{ fontSize:11, color:'#888', lineHeight:1.4 }}>
            <span style={{ fontSize:9, color:'#444', letterSpacing:1 }}>GESAMT · </span>{scoreText}
            {hrv_trend && <div style={{ fontSize:10, color:'#444', marginTop:2 }}>Erholung: {hrv_trend}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Grün ≥75%, Gelb 50-74%, Orange <50% — einheitliche Score-Farbkonvention
function scoreAmpel(score) {
  return score >= 75 ? '#4a9c6f' : score >= 50 ? '#d4b83a' : '#d4823a';
}

const WOCHENTAGE_KURZ = ['Mo','Di','Mi','Do','Fr','Sa','So'];

// ─── "Diese Woche" — Vergangenheit real, Zukunft score-basiert ──────────────
function DieseWoche({ zielId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiFetch(`${API}/ziele/${zielId}/wochenplan`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, [zielId]);

  if (!data || !data.tage) return null;

  return (
    <div style={{ background:'#141414', borderRadius:8, padding:'12px 14px', display:'flex', flexDirection:'column', gap:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:9, color:'#555', letterSpacing:2 }}>DIESE WOCHE</div>
        {data.overload && (
          <span style={{ fontSize:9, color:'#c17f24', border:'1px solid #3a2a10', borderRadius:3, padding:'1px 6px' }}>
            Erholung empfohlen
          </span>
        )}
      </div>
      <div style={{ display:'flex', gap:5, overflowX:'auto', paddingBottom:2 }}>
        {data.tage.map((tag, i) => {
          const hatAkt = tag.aktivitaeten.length > 0 || tag.gym.length > 0;
          return (
            <div key={tag.datum} style={{
              minWidth: tag.ist_heute ? 92 : 74, flex: tag.ist_heute ? '0 0 92px' : '0 0 74px',
              background: tag.ist_heute ? '#0a1420' : '#0d0d0d',
              border: '1px solid ' + (tag.ist_heute ? '#3a6a9a' : '#1e1e1e'),
              borderRadius:7, padding:'7px 6px', display:'flex', flexDirection:'column', gap:4,
            }}>
              <div style={{ textAlign:'center', fontSize: tag.ist_heute ? 11 : 10, fontWeight: tag.ist_heute ? 700 : 400, color: tag.ist_heute ? '#5a8fd4' : '#777' }}>
                {WOCHENTAGE_KURZ[i]}
              </div>

              {hatAkt && tag.aktivitaeten.map((a, j) => (
                <div key={j} style={{ background:'#1a1a2a', borderRadius:4, padding:'3px 4px' }}>
                  <div style={{ fontSize:8, color:'#5a8fd4', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {a.name.length > 12 ? a.name.slice(0, 11) + '…' : a.name}
                  </div>
                  <div style={{ fontSize:8, color:'#444' }}>
                    {a.dauer_min ? `${a.dauer_min}min` : ''}{a.distanz_km ? ` · ${a.distanz_km}km` : ''}
                  </div>
                </div>
              ))}
              {hatAkt && tag.gym.map((g, j) => (
                <div key={'g'+j} style={{ background:'#0a1a0a', borderRadius:4, padding:'3px 4px' }}>
                  <div style={{ fontSize:8, color:'#4a9c6f', fontWeight:600 }}>Gym</div>
                </div>
              ))}

              {!hatAkt && tag.vorschlag && (
                <div style={{ background:'#111', borderRadius:4, padding:'3px 4px', border:'1px dashed #2a2a2a' }}>
                  <div style={{ fontSize:8, color: tag.vorschlag.farbe, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {tag.vorschlag.label}
                  </div>
                  <div style={{ fontSize:8, color:'#333' }}>{tag.vorschlag.dauer}</div>
                </div>
              )}

              {!hatAkt && !tag.vorschlag && tag.ist_vergangenheit && (
                <div style={{ textAlign:'center', fontSize:8, color:'#2a2a2a' }}>—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SCHWACH_HINWEIS = {
  spitzenleistung: 'Ein langer Ride würde viel bringen.',
  trainingskontinuitaet: 'Etwas mehr Regelmäßigkeit bringt dich voran.',
  erholung: 'Heute eher ruhig angehen zahlt sich aus.',
};

function ermutigenderSatz(dimensionen) {
  if (!dimensionen) return null;
  const eintraege = Object.entries(dimensionen).filter(([, d]) => d.score != null);
  if (!eintraege.length) return null;
  const alleGut = eintraege.every(([, d]) => d.score >= 75);
  if (alleGut) return 'Du bist rundum gut aufgestellt.';
  const [schwaechsterKey] = eintraege.reduce((min, e) => e[1].score < min[1].score ? e : min);
  return SCHWACH_HINWEIS[schwaechsterKey] || null;
}

// ─── Kompakte Score-Übersicht (auf einen Blick, immer sichtbar) ──────────────
function ZielUebersichtZeile({ fortschritt, loading }) {
  if (loading) return <div style={{ fontSize:10, color:'#333' }}>Lade Fortschritt...</div>;
  if (!fortschritt) return null;

  const { dimensionen, bereitschafts_score } = fortschritt;
  const dims = [
    dimensionen.spitzenleistung,
    dimensionen.trainingskontinuitaet,
    dimensionen.erholung,
  ];
  if (bereitschafts_score == null) return null;
  const satz = ermutigenderSatz(dimensionen);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8, paddingTop:8, borderTop:'1px solid #1a1a1a' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        {/* Gesamtscore-Ring */}
        <div style={{
          width:34, height:34, borderRadius:'50%', flexShrink:0,
          border:`2px solid ${scoreAmpel(bereitschafts_score)}`, display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:10, fontWeight:700, color:scoreAmpel(bereitschafts_score),
        }}>
          {bereitschafts_score}%
        </div>
        {/* Drei Dimensionen nebeneinander */}
        <div style={{ display:'flex', gap:10, flex:1, minWidth:0 }}>
          {dims.map(d => (
            <div key={d.label} style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:8, color:'#555', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d.label}</div>
              <div style={{ fontSize:12, fontWeight:700, color: d.score != null ? scoreAmpel(d.score) : '#333' }}>
                {d.score != null ? `${d.score}%` : '–'}
              </div>
            </div>
          ))}
        </div>
      </div>
      {satz && <div style={{ fontSize:11, color:'#888' }}>{satz}</div>}
    </div>
  );
}

// ─── Ziel-Karte ───────────────────────────────────────────────────────────────
function ZielKarte({ z, onEdit, onDelete, onStatusChange, onModusToggle, onKiAnalyse, kiLoading, phasen, onPhasenLaden, onPhasenGenerieren, onPhaseDelete }) {
  const [offen, setOffen] = useState(false);
  const [fortschritt, setFortschritt] = useState(null);
  const [fortschrittLoading, setFortschrittLoading] = useState(true);
  const hatAnalyse = z.ki_analyse && typeof z.ki_analyse === 'object';
  const istBestaerkung = z.modus !== 'ziel';

  useEffect(() => {
    apiFetch(`${API}/ziele/${z.id}/fortschritt`)
      .then(r => r.json())
      .then(d => { setFortschritt(d); setFortschrittLoading(false); })
      .catch(() => setFortschrittLoading(false));
  }, [z.id]);

  function toggle() {
    setOffen(v => {
      if (!v && !phasen) onPhasenLaden(z.id);
      return !v;
    });
  }

  const datumStr = z.peak_datum
    ? new Date(z.peak_datum).toLocaleDateString('de-DE', { weekday:'short', day:'numeric', month:'long', year:'numeric', timeZone:'Europe/Berlin' })
    : 'Kein festes Datum';

  return (
    <div style={{ borderBottom:'1px solid #1c1c1c' }}>
      {/* Kopfzeile */}
      <div style={{ padding:'12px 16px', display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#e0e0e0' }}>{z.name}</div>
            <div style={{ fontSize:9, color: STATUS_FARBEN[z.status], border:`1px solid ${STATUS_FARBEN[z.status]}`, borderRadius:3, padding:'1px 5px' }}>
              {z.status}
            </div>
            {z.prioritaet === 1 && (
              <div style={{ fontSize:9, color:'#c17f24', border:'1px solid #c17f24', borderRadius:3, padding:'1px 5px' }}>★ Haupt</div>
            )}
            {/* Modus-Toggle */}
            <button
              onClick={() => onModusToggle(z.id, istBestaerkung ? 'ziel' : 'bestaerkung')}
              title={istBestaerkung ? 'Zu Ziel-Modus wechseln' : 'Zu Bestärkungs-Modus wechseln'}
              style={{
                background: istBestaerkung ? '#0a1a0a' : '#0a0d1a',
                border: `1px solid ${istBestaerkung ? '#2a5c2a' : '#2a3a6a'}`,
                borderRadius:3, padding:'1px 7px', fontSize:9,
                color: istBestaerkung ? '#4a9c6f' : '#5a8fd4',
                cursor:'pointer', fontFamily:'inherit',
              }}
            >
              {istBestaerkung ? '◯ Bestärkung' : '▸ Ziel'}
            </button>
          </div>
          {z.beschreibung && <div style={{ fontSize:11, color:'#555', marginBottom:3 }}>{z.beschreibung}</div>}
          <div style={{ fontSize:11, color:'#444' }}>
            {datumStr}
            {/* Countdown nur im Ziel-Modus */}
            {!istBestaerkung && z.tage_bis != null && z.peak_datum && (
              <span style={{ marginLeft:8, color: z.tage_bis < 14 ? '#c17f24' : '#5a8fd4', fontWeight:600 }}>
                {z.tage_bis > 0 ? `noch ${z.tage_bis}d` : z.tage_bis === 0 ? 'heute' : `vor ${Math.abs(z.tage_bis)}d`}
              </span>
            )}
            {/* Anforderung (km/hm) — unabhängig vom Modus, wenn hinterlegt */}
            {(z.ziel_km || z.ziel_hm) && (
              <span style={{ marginLeft:8, color:'#4a9c6f', fontSize:10 }}>
                {z.ziel_km ? `${z.ziel_km} km` : ''}{z.ziel_km && z.ziel_hm ? ' · ' : ''}{z.ziel_hm ? `${z.ziel_hm} hm` : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
          <button onClick={toggle} style={{ background:'#1a1a2a', border:'1px solid #2a2a4a', borderRadius:4, color:'#5a8fd4', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
            {offen ? '▲' : '▼ Details'}
          </button>
          <div style={{ display:'flex', gap:4 }}>
            <button onClick={() => onEdit(z)} style={{ background:'#1a1a1a', border:'none', borderRadius:4, color:'#666', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
              Bearbeiten
            </button>
            <button onClick={() => onDelete(z.id)} style={{ background:'transparent', border:'none', borderRadius:4, color:'#4a2020', fontSize:13, padding:'4px 6px', cursor:'pointer', lineHeight:1 }}>×</button>
          </div>
        </div>
      </div>

      {/* Auf einen Blick: immer sichtbar, unabhängig vom Ausklapp-Zustand */}
      <div style={{ padding:'0 16px 12px' }}>
        <ZielUebersichtZeile fortschritt={fortschritt} loading={fortschrittLoading} />
      </div>

      {/* Ausgeklappt: Detailansicht (getippt über "Details"-Button oben) */}
      {offen && (
        <div style={{ background:'#0d0d0d', padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>

          {/* Fortschritts-Detailkarte (nur im Bestärkungs-Modus) */}
          {istBestaerkung && <FortschrittsKarte data={fortschritt} />}
          {istBestaerkung && <DieseWoche zielId={z.id} />}

          {/* Ziel-Modus: Fortschrittsinfo */}
          {!istBestaerkung && z.peak_datum && z.tage_bis != null && z.tage_bis > 0 && (
            <div style={{ background:'#0a0d1a', border:'1px solid #1a2a4a', borderRadius:6, padding:'10px 14px' }}>
              <div style={{ fontSize:9, color:'#5a8fd4', letterSpacing:2, marginBottom:6 }}>VORBEREITUNG</div>
              <div style={{ fontSize:13, color:'#c0d0e0' }}>
                Noch {z.tage_bis} Tage bis {z.name}.
              </div>
              {(z.ziel_km || z.ziel_hm) && (
                <div style={{ fontSize:11, color:'#555', marginTop:4 }}>
                  Anforderung: {z.ziel_km ? `${z.ziel_km} km` : ''}{z.ziel_km && z.ziel_hm ? ' / ' : ''}{z.ziel_hm ? `${z.ziel_hm} hm` : ''}
                </div>
              )}
            </div>
          )}

          {/* KI-Analyse */}
          {hatAnalyse && (
            <div style={{ background:'#0a0d16', border:'1px solid #1a2a4a', borderRadius:6, padding:'10px 12px' }}>
              <div style={{ fontSize:9, color:'#5a8fd4', letterSpacing:2, marginBottom:6 }}>KI-ANALYSE</div>
              <div style={{ fontSize:12, color:'#c0d0e0', marginBottom:6 }}>{z.ki_analyse.zusammenfassung}</div>
              {z.ki_analyse.anforderungen?.length > 0 && (
                <div style={{ marginBottom:6 }}>
                  <div style={{ fontSize:9, color:'#555', marginBottom:3 }}>ANFORDERUNGEN</div>
                  {z.ki_analyse.anforderungen.map((a, i) => (
                    <div key={i} style={{ fontSize:11, color:'#888', paddingLeft:8 }}>· {a}</div>
                  ))}
                </div>
              )}
              {z.ki_analyse.schwerpunkte?.length > 0 && (
                <div>
                  <div style={{ fontSize:9, color:'#555', marginBottom:3 }}>TRAININGSSCHWERPUNKTE</div>
                  {z.ki_analyse.schwerpunkte.map((s, i) => (
                    <div key={i} style={{ fontSize:11, color:'#4a9c6f', paddingLeft:8 }}>→ {s}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => onKiAnalyse(z.id)}
            disabled={kiLoading}
            style={{ background: kiLoading ? '#111' : '#0d1a2a', border:'1px solid #1a3a5a', borderRadius:5, color: kiLoading ? '#444' : '#5a8fd4', fontSize:12, padding:'8px 12px', cursor: kiLoading ? 'default' : 'pointer', fontFamily:'inherit', textAlign:'left' }}
          >
            {kiLoading ? '⏳ KI analysiert...' : hatAnalyse ? '↻ Analyse aktualisieren' : '✦ KI-Analyse starten'}
          </button>

          {/* Phasen (sinnvoll im Ziel-Modus) */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:9, color:'#555', letterSpacing:2 }}>TRAININGSPHASEN</div>
              {hatAnalyse && z.ki_analyse.phasen?.length > 0 && (
                <button onClick={() => onPhasenGenerieren(z.id)}
                  style={{ background:'#1a2a1a', border:'1px solid #2a4a2a', borderRadius:4, color:'#4a9c6f', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
                  ⚡ Aus KI generieren
                </button>
              )}
            </div>
            {!phasen && <div style={{ fontSize:11, color:'#444' }}>Wird geladen...</div>}
            {phasen?.length === 0 && <div style={{ fontSize:11, color:'#333' }}>Noch keine Phasen definiert</div>}
            {phasen?.map(p => (
              <div key={p.id} style={{ background:'#111', borderRadius:5, padding:'8px 10px', marginBottom:6, display:'grid', gridTemplateColumns:'1fr auto', gap:6 }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#c0d0e0' }}>{p.name}</div>
                  <div style={{ fontSize:10, color:'#555' }}>
                    {new Date(p.von_datum).toLocaleDateString('de-DE', { day:'numeric', month:'short', timeZone:'Europe/Berlin' })}
                    {' – '}
                    {new Date(p.bis_datum).toLocaleDateString('de-DE', { day:'numeric', month:'short', year:'numeric', timeZone:'Europe/Berlin' })}
                  </div>
                  {p.beschreibung && <div style={{ fontSize:11, color:'#666', marginTop:2 }}>{p.beschreibung}</div>}
                  {p.empfehlung_rad && <div style={{ fontSize:10, color:'#5a8fd4', marginTop:2 }}>🚴 {p.empfehlung_rad}</div>}
                  {p.empfehlung_gym && <div style={{ fontSize:10, color:'#4a9c6f', marginTop:2 }}>💪 {p.empfehlung_gym}</div>}
                </div>
                <button onClick={() => onPhaseDelete(p.id, z.id)}
                  style={{ background:'transparent', border:'none', color:'#3a2020', fontSize:14, cursor:'pointer', padding:'0 4px', alignSelf:'start' }}>×</button>
              </div>
            ))}
          </div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4, borderTop:'1px solid #1c1c1c' }}>
            {z.status === 'aktiv' && (
              <button onClick={() => onStatusChange(z.id, 'abgeschlossen')}
                style={{ background:'#1a2a1a', border:'1px solid #2a4a2a', borderRadius:4, color:'#4a9c6f', fontSize:11, padding:'5px 10px', cursor:'pointer', fontFamily:'inherit' }}>
                ✓ Abgeschlossen
              </button>
            )}
            {z.status !== 'aktiv' && (
              <button onClick={() => onStatusChange(z.id, 'aktiv')}
                style={{ background:'#1a2a3a', border:'1px solid #2a3a5a', borderRadius:4, color:'#5a8fd4', fontSize:11, padding:'5px 10px', cursor:'pointer', fontFamily:'inherit' }}>
                Reaktivieren
              </button>
            )}
            <button onClick={() => onDelete(z.id)}
              style={{ background:'#2a1a1a', border:'none', borderRadius:4, color:'#c05050', fontSize:11, padding:'5px 10px', cursor:'pointer', fontFamily:'inherit' }}>
              Löschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit-Formular ────────────────────────────────────────────────────────────
function EditZielForm({ ziel, onSave, onAbbrechen }) {
  const [form, setForm] = useState({
    name: ziel.name,
    datum: ziel.peak_datum?.slice(0,10) || '',
    keinDatum: !ziel.peak_datum,
    beschreibung: ziel.beschreibung || '',
    prioritaet: ziel.prioritaet || 1,
    status: ziel.status || 'aktiv',
    modus: ziel.modus || 'bestaerkung',
    ziel_km: ziel.ziel_km || '',
    ziel_hm: ziel.ziel_hm || '',
  });

  function save() {
    onSave({
      name: form.name,
      peak_datum: form.keinDatum ? null : (form.datum || null),
      beschreibung: form.beschreibung || null,
      prioritaet: form.prioritaet,
      status: form.status,
      modus: form.modus,
      ziel_km: form.ziel_km ? parseFloat(form.ziel_km) : null,
      ziel_hm: form.ziel_hm ? parseFloat(form.ziel_hm) : null,
    });
  }

  return (
    <div style={{ padding:'12px 16px', background:'#0d0d0d', borderBottom:'1px solid #1c1c1c' }}>
      <input value={form.name} onChange={e => setForm(v => ({...v, name: e.target.value}))}
        style={{...INPUT, marginBottom:8}} placeholder="Zielname" />

      {/* Modus */}
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, color:'#555', marginBottom:4 }}>MODUS</div>
        <div style={{ display:'flex', gap:6 }}>
          {[{v:'bestaerkung', l:'◯ Bestärkung', c:'#2a5c2a'}, {v:'ziel', l:'▸ Ziel', c:'#2a3a6a'}].map(m => (
            <button key={m.v} onClick={() => setForm(f => ({...f, modus: m.v}))}
              style={BTN(form.modus === m.v, m.c)}>
              {m.l}
            </button>
          ))}
        </div>
        <div style={{ fontSize:10, color:'#444', marginTop:4 }}>
          {form.modus === 'bestaerkung'
            ? 'Zeigt Bereitschaft und was du schon geschafft hast — kein Countdown, kein Druck.'
            : 'Zeigt Countdown und Fortschritt zum Zieldatum.'}
        </div>
      </div>

      {/* Ziel-Anforderungen (für Bereitschafts-Indikator) */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
        <label style={{ fontSize:10, color:'#555' }}>
          Ziel-Distanz (km, optional)
          <input type="number" step="0.1" value={form.ziel_km} onChange={e => setForm(f => ({...f, ziel_km: e.target.value}))} style={INPUT} placeholder="z.B. 159" />
        </label>
        <label style={{ fontSize:10, color:'#555' }}>
          Ziel-Höhenmeter (optional)
          <input type="number" step="10" value={form.ziel_hm} onChange={e => setForm(f => ({...f, ziel_hm: e.target.value}))} style={INPUT} placeholder="z.B. 2890" />
        </label>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
        <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#555', cursor:'pointer' }}>
          <input type="checkbox" checked={form.keinDatum} onChange={e => setForm(v => ({...v, keinDatum: e.target.checked}))} />
          Kein festes Datum
        </label>
        {!form.keinDatum && (
          <input type="date" value={form.datum} onChange={e => setForm(v => ({...v, datum: e.target.value}))}
            style={{...INPUT, marginTop:0, flex:1}} />
        )}
      </div>
      <input value={form.beschreibung} onChange={e => setForm(v => ({...v, beschreibung: e.target.value}))}
        style={{...INPUT, marginBottom:8}} placeholder="Beschreibung (optional)" />
      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
        {[1,2].map(p => (
          <button key={p} onClick={() => setForm(v => ({...v, prioritaet: p}))}
            style={BTN(form.prioritaet === p, p === 1 ? '#6a4a1a' : '#1a3a5a')}>
            {PRIORITAET_LABELS[p]}
          </button>
        ))}
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={save} disabled={!form.name} style={BTN(!!form.name, '#2a4a6f')}>Speichern</button>
        <button onClick={onAbbrechen}
          style={{ background:'#222', color:'#888', border:'none', borderRadius:5, padding:'6px 12px', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>Abbrechen</button>
      </div>
    </div>
  );
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────
export default function Ziele({ onClose }) {
  const [ziele, setZiele] = useState([]);
  const [ftpVerlauf, setFtpVerlauf] = useState([]);
  const [editZiel, setEditZiel] = useState(null);
  const [phasenMap, setPhasenMap] = useState({});
  const [kiLoading, setKiLoading] = useState(null);
  const [kiAnalyseNeu, setKiAnalyseNeu] = useState(null);

  const [zielForm, setZielForm] = useState({ name:'', datum:'', keinDatum:false, beschreibung:'', prioritaet:1, modus:'bestaerkung', ziel_km:'', ziel_hm:'' });
  const [zielStatus, setZielStatus] = useState('idle');

  const [ftpForm, setFtpForm] = useState({ datum: localDate(), watt:'', notiz:'' });
  const [ftpStatus, setFtpStatus] = useState('idle');
  const [ftpAnalyse, setFtpAnalyse] = useState(null);
  const [ftpAnalyseLoading, setFtpAnalyseLoading] = useState(false);

  function reload() {
    apiFetch(API + '/ziele').then(r => r.json()).then(setZiele).catch(() => {});
    apiFetch(API + '/rad/ftp').then(r => r.json()).then(setFtpVerlauf).catch(() => {});
  }

  useEffect(() => { reload(); }, []);

  function saveZiel() {
    const { name, datum, keinDatum, beschreibung, prioritaet, modus, ziel_km, ziel_hm } = zielForm;
    if (!name) return;
    setZielStatus('saving');
    apiFetch(API + '/ziele', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name, peak_datum: keinDatum ? null : (datum || null),
        beschreibung: beschreibung || null, prioritaet, modus,
        ziel_km: ziel_km ? parseFloat(ziel_km) : null,
        ziel_hm: ziel_hm ? parseFloat(ziel_hm) : null,
      }),
    })
      .then(r => r.json())
      .then(d => {
        setZielStatus('saved');
        reload();
        setZielForm({ name:'', datum:'', keinDatum:false, beschreibung:'', prioritaet:1, modus:'bestaerkung', ziel_km:'', ziel_hm:'' });
        setTimeout(() => setZielStatus('idle'), 1500);
        starteKiAnalyse(d.id);
      })
      .catch(() => { setZielStatus('error'); setTimeout(() => setZielStatus('idle'), 2000); });
  }

  function updateZiel(id, felder) {
    apiFetch(API + '/ziele/' + id, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(felder),
    }).then(() => { setEditZiel(null); reload(); });
  }

  function modusToggle(id, neuerModus) {
    apiFetch(API + '/ziele/' + id + '/modus', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modus: neuerModus }),
    }).then(() => reload());
  }

  function deleteZiel(id) {
    if (!window.confirm('Ziel wirklich löschen?')) return;
    apiFetch(API + '/ziele/' + id, { method:'DELETE' }).then(() => reload());
  }

  function statusAendern(id, status) {
    const z = ziele.find(x => x.id === id);
    if (!z) return;
    updateZiel(id, { name:z.name, peak_datum: z.peak_datum?.slice?.(0,10) || null, beschreibung:z.beschreibung||null, prioritaet:z.prioritaet||1, status, modus:z.modus||'bestaerkung', ziel_km:z.ziel_km||null, ziel_hm:z.ziel_hm||null });
  }

  function starteKiAnalyse(zielId) {
    setKiLoading(zielId);
    setKiAnalyseNeu(null);
    apiFetch(API + '/ziele/' + zielId + '/ki-analyse', { method:'POST' })
      .then(r => r.json())
      .then(d => { setKiLoading(null); setKiAnalyseNeu({ zielId, analyse: d.analyse }); reload(); })
      .catch(() => setKiLoading(null));
  }

  function ladePhasen(zielId) {
    apiFetch(API + '/ziele/' + zielId + '/phasen')
      .then(r => r.json())
      .then(p => setPhasenMap(m => ({...m, [zielId]: p})));
  }

  function phasenGenerieren(zielId) {
    apiFetch(API + '/ziele/' + zielId + '/phasen-generieren', { method:'POST' })
      .then(r => r.json())
      .then(() => ladePhasen(zielId));
  }

  function phaseLoeschen(phaseId, zielId) {
    apiFetch(API + '/ziele/phasen/' + phaseId, { method:'DELETE' })
      .then(() => ladePhasen(zielId));
  }

  function saveFtp() {
    const { datum, watt, notiz } = ftpForm;
    if (!datum || !watt) return;
    setFtpStatus('saving');
    const ftpAlt = ftpVerlauf[0]?.ftp_watt;
    const naechstesZiel = ziele.find(z => z.status==='aktiv' && z.tage_bis > 0);
    apiFetch(API + '/rad/ftp', {
      method:'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ datum, ftp_watt: parseInt(watt), notiz }),
    })
      .then(r => r.json())
      .then(() => {
        setFtpStatus('saved');
        reload();
        setFtpForm(f => ({...f, watt:'', notiz:''}));
        setTimeout(() => setFtpStatus('idle'), 2000);
        if (naechstesZiel || watt) {
          setFtpAnalyseLoading(true);
          setFtpAnalyse(null);
          apiFetch(API + '/ziele/training-analyse', {
            method:'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ziel_name: naechstesZiel?.name, ziel_datum: naechstesZiel?.peak_datum?.slice(0,10), ziel_typ: naechstesZiel?.typ, ftp_neu: parseInt(watt), ftp_alt: ftpAlt }),
          })
            .then(r => r.json())
            .then(d => { setFtpAnalyse(d.analyse); setFtpAnalyseLoading(false); })
            .catch(() => setFtpAnalyseLoading(false));
        }
      })
      .catch(() => { setFtpStatus('error'); setTimeout(() => setFtpStatus('idle'), 2000); });
  }

  function deleteFtp(id) {
    if (!window.confirm('FTP-Eintrag wirklich löschen?')) return;
    apiFetch(API + '/rad/ftp/' + id, { method:'DELETE' }).then(() => reload());
  }

  const aktiveFtp = ftpVerlauf[0]?.ftp_watt;
  const aktiveZiele = ziele.filter(z => z.status === 'aktiv');
  const andereZiele = ziele.filter(z => z.status !== 'aktiv');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#c17f24' }}>Ziele & Training</div>
        {onClose && (
          <button onClick={onClose} style={{ background:'transparent', border:'1px solid #2a2a2a', borderRadius:6, color:'#666', fontSize:12, padding:'4px 12px', cursor:'pointer', fontFamily:'inherit' }}>
            Zurück
          </button>
        )}
      </div>

      {/* Aktive Ziele */}
      <div style={{ background:'#141414', borderRadius:8, overflow:'hidden' }}>
        <div style={{ padding:'10px 16px', background:'#1c1c1c', borderBottom:'1px solid #222', fontSize:11, color:'#888', letterSpacing:2 }}>AKTIVE ZIELE</div>
        {aktiveZiele.length === 0 && <div style={{ padding:'14px 16px', fontSize:12, color:'#444' }}>Keine aktiven Ziele</div>}
        {aktiveZiele.map(z =>
          editZiel?.id === z.id ? (
            <EditZielForm key={z.id} ziel={z}
              onSave={felder => updateZiel(z.id, felder)}
              onAbbrechen={() => setEditZiel(null)} />
          ) : (
            <ZielKarte key={z.id}
              z={{...z, ki_analyse: z.ki_analyse ? (typeof z.ki_analyse === 'string' ? JSON.parse(z.ki_analyse) : z.ki_analyse) : null}}
              onEdit={setEditZiel}
              onDelete={deleteZiel}
              onStatusChange={statusAendern}
              onModusToggle={modusToggle}
              onKiAnalyse={starteKiAnalyse}
              kiLoading={kiLoading === z.id}
              phasen={phasenMap[z.id]}
              onPhasenLaden={ladePhasen}
              onPhasenGenerieren={phasenGenerieren}
              onPhaseDelete={phaseLoeschen}
            />
          )
        )}
      </div>

      {/* KI-Analyse Ergebnis */}
      {kiAnalyseNeu && (
        <div style={{ background:'#0a0d16', border:'1px solid #2a4a6a', borderRadius:8, padding:'14px 16px' }}>
          <div style={{ fontSize:10, color:'#5a8fd4', letterSpacing:2, marginBottom:10 }}>KI-ANALYSE — NEUES ZIEL</div>
          {kiAnalyseNeu.analyse?.zusammenfassung && (
            <div style={{ fontSize:13, color:'#c0d0e0', marginBottom:10, lineHeight:1.6 }}>{kiAnalyseNeu.analyse.zusammenfassung}</div>
          )}
          {kiAnalyseNeu.analyse?.schwerpunkte?.length > 0 && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:9, color:'#555', marginBottom:4 }}>TRAININGSSCHWERPUNKTE</div>
              {kiAnalyseNeu.analyse.schwerpunkte.map((s, i) => (
                <div key={i} style={{ fontSize:12, color:'#4a9c6f', paddingLeft:8, marginBottom:2 }}>→ {s}</div>
              ))}
            </div>
          )}
          {kiAnalyseNeu.analyse?.phasen?.length > 0 && (
            <div>
              <div style={{ fontSize:9, color:'#555', marginBottom:4 }}>VORGESCHLAGENE PHASEN</div>
              {kiAnalyseNeu.analyse.phasen.map((p, i) => (
                <div key={i} style={{ fontSize:11, color:'#888', paddingLeft:8, marginBottom:2 }}>
                  <span style={{ color:'#c0d0e0', fontWeight:600 }}>{p.name}</span> ({p.wochen} Wo) — {p.fokus}
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { phasenGenerieren(kiAnalyseNeu.zielId); setKiAnalyseNeu(null); }}
            style={{ marginTop:12, background:'#1a2a3a', border:'1px solid #2a4a6a', borderRadius:5, color:'#5a8fd4', fontSize:12, padding:'8px 14px', cursor:'pointer', fontFamily:'inherit' }}>
            ⚡ Phasen generieren
          </button>
          <button onClick={() => setKiAnalyseNeu(null)}
            style={{ marginTop:12, marginLeft:8, background:'transparent', border:'none', color:'#444', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
            Schließen
          </button>
        </div>
      )}

      {/* Neues Ziel */}
      <div style={{ background:'#141414', borderRadius:8, padding:'14px 16px' }}>
        <div style={{ fontSize:11, color:'#888', letterSpacing:2, marginBottom:12 }}>NEUES ZIEL</div>
        <input type="text" placeholder="Zielname (z.B. Orbit360 Edersee, Halbmarathon...)" value={zielForm.name}
          onChange={e => setZielForm(f => ({...f, name: e.target.value}))}
          style={{ width:'100%', background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'8px 10px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:8 }} />
        <input type="text" placeholder="Beschreibung (optional)" value={zielForm.beschreibung}
          onChange={e => setZielForm(f => ({...f, beschreibung: e.target.value}))}
          style={{ width:'100%', background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:8 }} />

        {/* Modus */}
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:10, color:'#555', marginBottom:4 }}>MODUS</div>
          <div style={{ display:'flex', gap:6 }}>
            {[{v:'bestaerkung', l:'◯ Bestärkung', c:'#2a5c2a'}, {v:'ziel', l:'▸ Ziel', c:'#2a3a6a'}].map(m => (
              <button key={m.v} onClick={() => setZielForm(f => ({...f, modus: m.v}))}
                style={BTN(zielForm.modus === m.v, m.c)}>
                {m.l}
              </button>
            ))}
          </div>
        </div>

        {/* Anforderungen (optional) */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
          <label style={{ fontSize:10, color:'#555' }}>
            Ziel-Distanz km (optional)
            <input type="number" step="0.1" value={zielForm.ziel_km} onChange={e => setZielForm(f => ({...f, ziel_km: e.target.value}))}
              style={{ background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'6px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', marginTop:3, width:'100%', boxSizing:'border-box' }} placeholder="z.B. 159" />
          </label>
          <label style={{ fontSize:10, color:'#555' }}>
            Höhenmeter (optional)
            <input type="number" step="10" value={zielForm.ziel_hm} onChange={e => setZielForm(f => ({...f, ziel_hm: e.target.value}))}
              style={{ background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'6px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', marginTop:3, width:'100%', boxSizing:'border-box' }} placeholder="z.B. 2890" />
          </label>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#555', cursor:'pointer' }}>
            <input type="checkbox" checked={zielForm.keinDatum} onChange={e => setZielForm(f => ({...f, keinDatum: e.target.checked}))} />
            Kein festes Datum
          </label>
          {!zielForm.keinDatum && (
            <input type="date" value={zielForm.datum} onChange={e => setZielForm(f => ({...f, datum: e.target.value}))}
              style={{ background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'6px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none' }} />
          )}
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          {[1,2].map(p => (
            <button key={p} onClick={() => setZielForm(f => ({...f, prioritaet: p}))}
              style={BTN(zielForm.prioritaet === p, p === 1 ? '#6a4a1a' : '#1a3a5a')}>
              {PRIORITAET_LABELS[p]}
            </button>
          ))}
        </div>
        <button onClick={saveZiel} disabled={!zielForm.name || zielStatus === 'saving'}
          style={{ background: zielStatus === 'saved' ? '#2a5c3a' : zielStatus === 'error' ? '#5c2a2a' : zielForm.name ? '#2a4a6f' : '#1a1a1a', color: zielForm.name ? '#fff' : '#444', border:'none', borderRadius:6, padding:'10px 20px', fontSize:13, fontWeight:700, cursor: zielForm.name ? 'pointer' : 'default', fontFamily:'inherit' }}>
          {zielStatus === 'saved' ? '✓ Gespeichert — KI analysiert...' : zielStatus === 'saving' ? 'Speichere...' : 'Ziel speichern & KI-Analyse starten'}
        </button>
      </div>

      {/* Abgeschlossene / Pausierte */}
      {andereZiele.length > 0 && (
        <div style={{ background:'#141414', borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'10px 16px', background:'#1c1c1c', borderBottom:'1px solid #222', fontSize:11, color:'#555', letterSpacing:2 }}>ABGESCHLOSSEN / PAUSIERT</div>
          {andereZiele.map(z => {
            const abgelaufen = z.status === 'abgeschlossen' && z.peak_datum && z.tage_bis < 0;
            return (
              <div key={z.id} style={{ padding:'10px 16px', borderBottom:'1px solid #1c1c1c', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ fontSize:13, color: abgelaufen ? '#664a2a' : z.status === 'abgeschlossen' ? '#4a9c6f' : '#555' }}>
                      {z.status === 'abgeschlossen' && !abgelaufen ? '✓ ' : ''}{z.name}
                    </div>
                    {abgelaufen && <div style={{ fontSize:9, color:'#664a2a', border:'1px solid #664a2a', borderRadius:3, padding:'1px 5px' }}>abgelaufen</div>}
                  </div>
                  {z.peak_datum && <div style={{ fontSize:11, color:'#333', marginTop:2 }}>{new Date(z.peak_datum).toLocaleDateString('de-DE', { day:'numeric', month:'short', year:'numeric', timeZone:'Europe/Berlin' })}</div>}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => statusAendern(z.id, 'aktiv')}
                    style={{ background:'#1a2a3a', border:'none', borderRadius:4, color:'#5a8fd4', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
                    Reaktivieren
                  </button>
                  <button onClick={() => deleteZiel(z.id)}
                    style={{ background:'#2a1a1a', border:'none', borderRadius:4, color:'#c05050', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
                    Löschen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FTP-Verlauf */}
      <div style={{ background:'#141414', borderRadius:8, overflow:'hidden' }}>
        <div style={{ padding:'10px 16px', background:'#1c1c1c', borderBottom:'1px solid #222', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:11, color:'#888', letterSpacing:2 }}>FTP-VERLAUF</span>
          {aktiveFtp && <span style={{ fontSize:13, fontWeight:700, color:'#4a9c6f' }}>{aktiveFtp}W aktuell</span>}
        </div>
        {ftpVerlauf.slice(0, 5).map(f => (
          <div key={f.id} style={{ padding:'10px 16px', borderBottom:'1px solid #1c1c1c', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#4a9c6f' }}>{f.ftp_watt}W</div>
              {f.notiz && <div style={{ fontSize:11, color:'#555', marginTop:1 }}>{f.notiz}</div>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ fontSize:11, color:'#444' }}>{new Date(f.datum).toLocaleDateString('de-DE', { day:'numeric', month:'short', year:'numeric', timeZone:'Europe/Berlin' })}</div>
              <button onClick={() => deleteFtp(f.id)}
                style={{ background:'#2a1a1a', border:'none', borderRadius:4, color:'#c05050', fontSize:11, padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>Löschen</button>
            </div>
          </div>
        ))}
      </div>

      {/* FTP eintragen */}
      <div style={{ background:'#0a160a', border:'1px solid #2a5c2a', borderRadius:8, padding:'14px 16px' }}>
        <div style={{ fontSize:11, color:'#4a9c6f', letterSpacing:2, marginBottom:12 }}>FTP-TESTERGEBNIS EINTRAGEN</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
          <label style={{ fontSize:11, color:'#555' }}>
            Datum
            <input type="date" value={ftpForm.datum} onChange={e => setFtpForm(f => ({...f, datum: e.target.value}))} style={INPUT} />
          </label>
          <label style={{ fontSize:11, color:'#555' }}>
            FTP (Watt)*
            <input type="number" min="100" max="500" placeholder={aktiveFtp ? String(aktiveFtp) : '245'} value={ftpForm.watt}
              onChange={e => setFtpForm(f => ({...f, watt: e.target.value}))} style={INPUT} />
          </label>
        </div>
        <input type="text" placeholder="Notiz (z.B. Testergebnis, Bedingungen...)" value={ftpForm.notiz}
          onChange={e => setFtpForm(f => ({...f, notiz: e.target.value}))}
          style={{ width:'100%', background:'#111', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:10 }} />
        {ftpForm.watt && aktiveFtp && (
          <div style={{ fontSize:11, color: parseInt(ftpForm.watt) > aktiveFtp ? '#4a9c6f' : '#c17f24', marginBottom:8 }}>
            {parseInt(ftpForm.watt) > aktiveFtp
              ? `+${parseInt(ftpForm.watt) - aktiveFtp}W — neue Zone 2: ${Math.round(parseInt(ftpForm.watt)*0.56)}–${Math.round(parseInt(ftpForm.watt)*0.75)}W`
              : `${parseInt(ftpForm.watt) - aktiveFtp}W gegenüber bisher`}
          </div>
        )}
        <button onClick={saveFtp} disabled={!ftpForm.watt || ftpStatus === 'saving'}
          style={{ background: ftpStatus === 'saved' ? '#2a5c3a' : ftpStatus === 'error' ? '#5c2a2a' : ftpForm.watt ? '#2a5c2a' : '#1a1a1a', color: ftpForm.watt ? '#fff' : '#444', border:'none', borderRadius:6, padding:'10px 20px', fontSize:13, fontWeight:700, cursor: ftpForm.watt ? 'pointer' : 'default', fontFamily:'inherit' }}>
          {ftpStatus === 'saved' ? 'Gespeichert' : ftpStatus === 'saving' ? 'Speichere...' : 'FTP speichern & Trainingsplan anpassen'}
        </button>
      </div>

      {(ftpAnalyseLoading || ftpAnalyse) && (
        <div style={{ background:'#0a0d16', border:'1px solid #1a2a4a', borderRadius:8, padding:'14px 16px' }}>
          <div style={{ fontSize:10, color:'#5a8fd4', letterSpacing:2, marginBottom:10 }}>TRAININGSPLAN-ANPASSUNG</div>
          {ftpAnalyseLoading ? (
            <div style={{ fontSize:13, color:'#4a6fa8' }}>KI analysiert neues Training...</div>
          ) : (
            <div style={{ fontSize:13, color:'#c0d0e0', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{ftpAnalyse?.replace(/\*\*/g, '')}</div>
          )}
        </div>
      )}
    </div>
  );
}
